/**
 * React Query hooks for WarmPool operations
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { warmPoolsApi } from '@/lib/api/warmnodepools';
import {
  WarmPool,
  WarmPoolCreateData,
  WarmPoolListParams,
  WarmPoolListResponse,
  WarmPoolMode,
  WarmPoolUpdateData,
} from '../types/warmpool';

// Query keys factory for consistent cache management
export const warmPoolKeys = {
  all: ['warmpools'] as const,
  lists: () => [...warmPoolKeys.all, 'list'] as const,
  list: (params?: WarmPoolListParams) => [...warmPoolKeys.lists(), params] as const,
  details: () => [...warmPoolKeys.all, 'detail'] as const,
  detail: (name: string) => [...warmPoolKeys.details(), name] as const,
};

/**
 * Hook to fetch list of warm pools
 */
export function useWarmPools(params?: WarmPoolListParams) {
  return useQuery({
    queryKey: warmPoolKeys.list(params),
    queryFn: async () => {
      const response = await warmPoolsApi.list(params);

      // Handle multiple response formats
      const responseWithData = response as WarmPoolListResponse & {
        data?: WarmPoolListResponse;
      };
      const data = responseWithData.data || response;
      const items = data.items || (data as unknown as WarmPool[]);

      if (!Array.isArray(items)) {
        console.error('Invalid warm pool response format:', response);
        return { items: [], total: 0 };
      }

      return {
        items,
        total: data.total || items.length,
      };
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single warm pool by name
 */
export function useWarmPool(name: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: warmPoolKeys.detail(name),
    queryFn: () => warmPoolsApi.get(name),
    enabled: options?.enabled ?? !!name,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to create a new warm pool
 */
export function useCreateWarmPool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: WarmPoolCreateData }) => warmPoolsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.lists() });
    },
  });
}

/**
 * Hook to update an existing warm pool
 */
export function useUpdateWarmPool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, data }: { name: string; data: WarmPoolUpdateData }) =>
      warmPoolsApi.update(name, data),
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.detail(name) });
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.lists() });
    },
  });
}

/**
 * Hook to delete a warm pool
 */
export function useDeleteWarmPool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, force }: { name: string; force?: boolean }) =>
      warmPoolsApi.delete(name, force),
    onSuccess: (_, { name }) => {
      queryClient.removeQueries({ queryKey: warmPoolKeys.detail(name) });
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.lists() });
    },
  });
}

/**
 * Hook to suspend a warm pool
 */
export function useSuspendWarmPool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) => warmPoolsApi.suspend(name),
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.detail(name) });
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.lists() });
    },
  });
}

/**
 * Hook to resume a warm pool
 */
export function useResumeWarmPool() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) => warmPoolsApi.resume(name),
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.detail(name) });
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.lists() });
    },
  });
}

/**
 * Hook to change a warm pool's mode (default <-> overflow).
 */
export function useChangeWarmPoolMode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, mode }: { name: string; mode: WarmPoolMode }) =>
      warmPoolsApi.changeMode(name, mode),
    onSuccess: (_, { name }) => {
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.detail(name) });
      queryClient.invalidateQueries({ queryKey: warmPoolKeys.lists() });
    },
  });
}
